import { first, get, keyBy } from 'lodash';

import { common } from '@gosupersimple/types';

import {
  isConversationCell,
  isExplorationMessage,
  normalizeConversationBlocks,
} from '@/core/cell/conversation-cell';
import { updateViewOptions } from '@/core/cell/view-options';
import {
  isCellWithPipeline,
  isCellWithVisualisations,
  isInvalidCell,
  setCellPipeline,
  setCellSort,
  setCellVisualisations,
  restoreInvalidCell,
} from '@/core/cell';

import { createBasePipeline } from '@/core/pipeline';
import { extractCombineSteps } from '@/core/operation/combine';

import { notNil } from '@/lib/utils';

import { ensureValidVisualisation, generateVisualisation } from '../components/visualisation/utils';
import { getModel } from '../model/utils';
import {
  InvalidOperationError,
  getAvailableRelations,
  getFieldsWithRelatedFields,
  getFinalState,
  getFinalStateOrThrow,
  getNextPipelineStateOrThrow,
  isRelationGrouping,
} from '../pipeline/state';
import {
  ensureFakeBaseModelExists,
  type FieldsOrGroups,
  findFieldFromGroups,
  flattenPipeline,
  getBaseModelId,
  getPipelineWithRollback,
} from '../pipeline/utils';
import {
  Cell,
  Conversation,
  Exploration,
  FilterOperation,
  FunnelOperation,
  GroupAggregateOperation,
  InvalidOperation,
  Metric,
  Model,
  Pipeline,
  PipelineOperation,
  PipelineState,
  QueryVariables,
} from '../types';
import { getCohortOperation } from '../edit-cohort/utils';
import { getFunnelOperation } from '../edit-funnel/utils';
import {
  getGroupableFields,
  resolveFunnelGroups,
} from '../edit-funnel/funnel-groupings-editor/utils';
import { resolveFieldConflicts } from './field-references';

export class InvalidPipelineReferenceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidPipelineReferenceError';
  }
}

export class InvalidModelReferenceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidModelReferenceError';
  }
}

export const restoreInvalidOperation = (
  operation: PipelineOperation,
): Exclude<PipelineOperation, InvalidOperation> =>
  operation.operation === 'invalid' ? operation.parameters.operation : operation;

const restoreInvalidCells = (exploration: Exploration): Exploration => {
  return {
    ...exploration,
    view: {
      ...exploration.view,
      cells: exploration.view.cells.map(restoreInvalidCell),
    },
  };
};

const restoreInvalidOperations = (exploration: Exploration): Exploration => {
  return {
    ...exploration,
    view: {
      ...exploration.view,
      cells: exploration.view.cells.map((cell) => {
        if ('pipeline' in cell) {
          return setCellPipeline(cell, {
            ...cell.pipeline,
            operations: cell.pipeline.operations.map(restoreInvalidOperation),
          });
        }
        return cell;
      }),
    },
  };
};

/**
 * Validate the base model of a pipeline exists.
 * Some operations contain their own pipeline, like funnels and cohorts. For these we validate model(s) in the inner pipeline instead.
 */
const validateBaseModel = (
  cell: Cell,
  ctx: {
    exploration: Exploration;
    models: Model[];
    metrics: Metric[];
  },
) => {
  if (!('pipeline' in cell)) {
    return;
  }
  switch (cell.kind) {
    case 'records': {
      const baseModelId = getBaseModelId(cell.pipeline, ctx.exploration);
      const flattenedPipeline = flattenPipeline(cell.pipeline, ctx.exploration);
      const models = ensureFakeBaseModelExists(ctx.models, flattenedPipeline);
      if (getModel(models, baseModelId) === undefined) {
        throw new InvalidModelReferenceError(
          `Model "${baseModelId}" not found or has insufficient permissions`,
        );
      }
      const firstOperation = first(cell.pipeline.operations);
      if (firstOperation?.operation === 'joinPipeline') {
        extractCombineSteps(firstOperation).forEach((step) => {
          const relatedBaseModelId = getBaseModelId(step.pipeline, ctx.exploration);
          if (getModel(models, relatedBaseModelId) === undefined) {
            throw new InvalidModelReferenceError(
              `Model "${relatedBaseModelId}" not found or has insufficient permissions`,
            );
          }
        });
      }
      break;
    }
    case 'cohort': {
      const operation = getCohortOperation(cell.pipeline);
      if (operation?.operation !== 'cohort') {
        break;
      }
      const baseModelId = getBaseModelId(operation.parameters.pipeline, ctx.exploration);
      if (getModel(ctx.models, baseModelId) === undefined) {
        throw new InvalidModelReferenceError(
          `Model "${baseModelId}" not found or has insufficient permissions`,
        );
      }
      break;
    }
    case 'funnel': {
      const operation = getFunnelOperation(cell.pipeline);
      operation.parameters.steps.forEach((step) => {
        const baseModelId = getBaseModelId(step.pipeline, ctx.exploration);
        if (getModel(ctx.models, baseModelId) === undefined) {
          throw new InvalidModelReferenceError(
            `Model "${baseModelId}" not found or has insufficient permissions`,
          );
        }
      });
    }
  }
};

export const ensureValidExploration = (
  exploration: Exploration,
  models: Model[],
  metrics: Metric[],
  variables: common.QueryVariables,
): Exploration => {
  const restoredExploration = restoreInvalidOperations(restoreInvalidCells(exploration));

  const validatedExploration = resolveFieldConflicts(
    {
      ...restoredExploration,
      view: {
        canvas: restoredExploration.view.canvas,
        cells: normalizeConversationBlocks(restoredExploration.view.cells).map((cell) =>
          ensureValidCell(cell, { exploration: restoredExploration, models, metrics, variables }),
        ),
        options: restoredExploration.view.options,
        tabs: restoredExploration.view.tabs,
      },
    },
    { models, metrics, variables },
  );

  const ctx = {
    exploration: validatedExploration,
    models,
    metrics,
    variables,
  };

  return {
    ...validatedExploration,
    view: {
      ...validatedExploration.view,
      cells: validatedExploration.view.cells
        .map((cell) => ensureValidSort(cell, ctx))
        .map((cell) => ensureValidCellViewOptions(cell, ctx)),
    },
  };
};

const ensureValidCell = (
  cell: Cell,
  ctx: {
    exploration: Exploration;
    models: Model[];
    metrics: Metric[];
    variables: common.QueryVariables;
  },
): Cell => {
  if (isInvalidCell(cell)) {
    return cell;
  }

  try {
    validateBaseModel(cell, ctx);
  } catch (e) {
    if (e instanceof InvalidModelReferenceError || e instanceof InvalidPipelineReferenceError) {
      return {
        id: cell.id,
        kind: 'invalid',
        ...('title' in cell ? { title: cell.title } : {}),
        message: e.message,
        viewOptions: cell.viewOptions,
        cell,
      };
    }
    throw e;
  }

  if (isCellWithPipeline(cell)) {
    cell = setCellPipeline(cell, ensureValidPipeline(cell.pipeline, ctx));
  }

  if (isCellWithVisualisations(cell)) {
    const rollbackIndex = cell.viewOptions?.rollback?.index;
    const pipeline =
      typeof rollbackIndex === 'number'
        ? getPipelineWithRollback(cell.pipeline, rollbackIndex)
        : cell.pipeline;
    cell = setCellVisualisations(
      cell,
      (cell.visualisations ?? []).map((visualisation) => {
        try {
          return ensureValidVisualisation(visualisation, {
            ...ctx,
            pipeline: ensureValidPipeline(flattenPipeline(pipeline, ctx.exploration), ctx),
          });
        } catch {
          return generateVisualisation(
            ensureValidPipeline(flattenPipeline(pipeline, ctx.exploration), ctx),
            ctx,
          );
        }
      }),
    );
  }

  return cell;
};

const ensureValidCellViewOptions = (
  cell: Cell,
  ctx: {
    exploration: Exploration;
    models: Model[];
    metrics: Metric[];
    variables: common.QueryVariables;
  },
): Cell => {
  if (!('viewOptions' in cell)) {
    return cell;
  }
  return ensureValidFieldOrder(cell, ctx);
};

const ensureValidFieldOrder = (
  cell: Cell,
  ctx: {
    exploration: Exploration;
    models: Model[];
    metrics: Metric[];
    variables: common.QueryVariables;
  },
): Cell => {
  const fieldOrder = cell.viewOptions?.fieldOrder;
  if (fieldOrder === undefined || !isCellWithPipeline(cell)) {
    return cell;
  }

  const { fields } = getFinalStateOrThrow(cell.pipeline, ctx);

  // Ensure keys are valid and within range in case fields have been removed
  const newIndexes = new Array(fields.length).fill(null);
  Object.entries(fieldOrder).forEach(([key, index]) => {
    if (!fields.some((f) => f.key === key)) {
      return;
    }
    const newIndex = index < fields.length ? index : newIndexes.lastIndexOf(null);
    newIndexes[newIndex] = key;
  });
  const cleanedFieldOrder = newIndexes.reduce<Record<string, number>>((acc, key, index) => {
    if (key !== null) {
      acc[key] = index;
    }
    return acc;
  }, {});

  return updateViewOptions(cell, {
    ...cell.viewOptions,
    fieldOrder: cleanedFieldOrder,
  });
};

const ensureValidSort = (
  cell: Cell,
  ctx: {
    exploration: Exploration;
    models: Model[];
    metrics: Metric[];
    variables: common.QueryVariables;
  },
): Cell => {
  if (!('sort' in cell) || (cell.sort?.length ?? 0) === 0) {
    return cell;
  }

  const rollbackIndex = get(cell.viewOptions, 'rollback', undefined)?.index;
  const pipeline = getPipelineWithRollback(cell.pipeline, rollbackIndex);
  const pipelineState = getFinalStateOrThrow(pipeline, ctx);

  return setCellSort(
    cell,
    cell.sort?.filter((sort) => pipelineState.fields.some((field) => field.key === sort.key)),
  );
};

const ensureValidGrouping = (group: common.Grouping, fields: FieldsOrGroups): common.Grouping => {
  const lookup = isRelationGrouping(group)
    ? { key: group.key, relation: { key: group.relation.key, baseModelId: group.relation.modelId } }
    : { key: group.key };
  const field = findFieldFromGroups(fields, lookup);
  if (field?.type !== 'Date') {
    const { precision: _precision, fill: _fill, ...rest } = group;
    return rest;
  }
  return group;
};

const ensureValidGroupAggregateOperation = (
  operation: GroupAggregateOperation,
  ctx: { pipelineState: PipelineState; models: Model[] },
): GroupAggregateOperation => {
  const fields = getFieldsWithRelatedFields(ctx.pipelineState, ctx.models);
  return {
    ...operation,
    parameters: {
      ...operation.parameters,
      groups: operation.parameters.groups.map((group) => ensureValidGrouping(group, fields)),
    },
  };
};

const ensureValidFunnelOperation = (
  operation: FunnelOperation,
  ctx: {
    exploration: Exploration;
    models: Model[];
    metrics: Metric[];
    variables: common.QueryVariables;
  },
): FunnelOperation => {
  if (operation.parameters.groups === undefined) {
    return operation;
  }
  // Funnel groups reference related-model fields via addRelatedColumn ops added to each step. Resolving the groups
  // gives us the RelationGrouping that points to the correct related field from getGroupableFields (that fakes relations via modelIds)
  const fields = getGroupableFields(operation.parameters.steps, ctx);
  const resolvedGroups = resolveFunnelGroups(operation, ctx);
  return {
    ...operation,
    parameters: {
      ...operation.parameters,
      groups: operation.parameters.groups.map((group, i) => {
        const validated = ensureValidGrouping(resolvedGroups[i], fields);
        const { precision: _precision, fill: _fill, ...rest } = group;
        // We have to retain the original group key, not the resolved one, but take precision and fill from the validation function.
        return {
          ...rest,
          ...('precision' in validated ? { precision: validated.precision } : {}),
          ...('fill' in validated ? { fill: validated.fill } : {}),
        };
      }),
    },
  };
};

// Rather quick hack to ensure that the filter relation is valid (SUP-4153). Should be revisited in the future.
const ensureValidFilterOperation = (
  operation: FilterOperation,
  ctx: {
    exploration: Exploration;
    pipelineState: PipelineState;
    models: Model[];
    metrics: Metric[];
    variables: common.QueryVariables;
    operationIndex: number;
  },
): FilterOperation => {
  if ('relation' in operation.parameters && operation.parameters.relation !== undefined) {
    const relationKey = operation.parameters.relation.key;
    const relationsMatchedByKey = ctx.pipelineState.relations.filter(
      ({ key }) => key === relationKey,
    );
    if (relationsMatchedByKey.length === 0) {
      throw new InvalidOperationError(`Relation ${relationKey} not found`);
    }

    const requiredModelId = operation.parameters.relation.modelId;
    if (requiredModelId === undefined && relationsMatchedByKey.length > 1) {
      throw new InvalidOperationError(
        `Relation ${relationKey} is ambiguous, please specify modelId`,
      );
    }

    const matchedRelation = relationsMatchedByKey.find(
      ({ baseModelId }) => baseModelId === requiredModelId,
    );
    if (matchedRelation === undefined) {
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          relation: {
            ...operation.parameters.relation,
            modelId: ctx.pipelineState.model.modelId,
          },
        },
      };
    }
  }
  return operation;
};

const ensureValidOperation = (
  operation: PipelineOperation,
  ctx: {
    exploration: Exploration;
    pipelineState: PipelineState;
    models: Model[];
    metrics: Metric[];
    variables: common.QueryVariables;
    operationIndex: number;
  },
): { operation: PipelineOperation; nextState: PipelineState } => {
  if (operation.operation === 'invalid') {
    return { operation, nextState: ctx.pipelineState };
  }
  try {
    switch (operation.operation) {
      case 'filter':
        operation = ensureValidFilterOperation(operation, ctx);
        break;
      case 'groupAggregate':
        operation = ensureValidGroupAggregateOperation(operation, ctx);
        break;
      case 'funnel':
        operation = ensureValidFunnelOperation(operation, ctx);
        break;
      default:
    }
    // Compute next state once: validates the op (throws on error) AND gives us
    // the state to thread into the following iteration without rebuilding.
    const nextStateWithoutRelations = getNextPipelineStateOrThrow(
      ctx.pipelineState,
      operation,
      ctx.operationIndex,
      ctx,
    );
    const nextState: PipelineState = {
      ...nextStateWithoutRelations,
      relations: getAvailableRelations(nextStateWithoutRelations.fields, ctx.models),
    };
    return { operation, nextState };
  } catch (e) {
    if (e instanceof InvalidOperationError || e instanceof InvalidPipelineReferenceError) {
      return {
        operation: {
          operation: 'invalid',
          parameters: {
            message: e.message,
            operation,
          },
        },
        // An invalid op contributes nothing to schema — keep state as-is.
        nextState: ctx.pipelineState,
      };
    }
    throw e;
  }
};

export const ensureValidPipeline = (
  pipeline: Pipeline,
  ctx: {
    exploration: Exploration;
    models: Model[];
    metrics: Metric[];
    variables: common.QueryVariables;
  },
): Pipeline => {
  const flattenedPipeline = flattenPipeline(pipeline, ctx.exploration);
  const baseModelId = flattenedPipeline.baseModelId;
  const parentPipelineLength = flattenedPipeline.operations.length - pipeline.operations.length;
  const parentPipeline = {
    ...flattenedPipeline,
    operations: flattenedPipeline.operations.slice(0, parentPipelineLength),
  };
  const parentOperations =
    parentPipelineLength > 0 ? ensureValidPipeline(parentPipeline, ctx).operations : [];

  // Compute the initial state ONCE (state after applying validated parent
  // operations) and then thread it forward through the cell's own operations.
  // The previous implementation rebuilt the state from scratch for every
  // operation, giving O(N²) expression parses per cell with N ops.
  let state = getFinalState(createBasePipeline({ baseModelId, operations: parentOperations }), ctx);
  const validatedOperations: PipelineOperation[] = new Array(pipeline.operations.length);
  for (let i = 0; i < pipeline.operations.length; i++) {
    const { operation, nextState } = ensureValidOperation(pipeline.operations[i], {
      ...ctx,
      pipelineState: state,
      operationIndex: parentPipelineLength + i,
    });
    validatedOperations[i] = operation;
    state = nextState;
  }

  return { ...pipeline, operations: validatedOperations };
};

/**
 * Ensure that all exploration messages in a conversation are valid.
 * @param conversation
 * @param ctx Context containing the exploration, models, metrics and parameters. Explotion must be validated beforehand.
 * @returns A conversation with all exploration messages validated.
 */
export const ensureValidConversation = (
  conversation: Conversation,
  ctx: {
    exploration: Exploration;
    models: Model[];
    metrics: Metric[];
    variables: QueryVariables;
  },
): Conversation => {
  const externalCells = ctx.exploration.view.cells.filter((cell) => !isConversationCell(cell));
  const internalCells = conversation.messages.flatMap((message) => {
    return isExplorationMessage(message) ? message.exploration.view.cells : [];
  });
  // Merge cells from the conversation and the exploration and validate them together to ensure consistency.
  const interimExploration = {
    ...ctx.exploration,
    view: {
      ...ctx.exploration.view,
      cells: [...externalCells, ...internalCells],
    },
  };
  const validCells = keyBy(
    ensureValidExploration(interimExploration, ctx.models, ctx.metrics, ctx.variables).view.cells,
    'id',
  );
  /**
   * Replace cells in exploration messages with the validated cells.
   * It relies that the external cells are valid.
   **/
  return {
    ...conversation,
    messages: conversation.messages.map((message) => {
      if (isExplorationMessage(message)) {
        return {
          ...message,
          exploration: {
            ...message.exploration,
            view: {
              ...message.exploration.view,
              cells: message.exploration.view.cells
                .map((cell) => validCells[cell.id])
                .filter(notNil),
            },
          },
        };
      }
      return message;
    }),
  };
};
